import { type ReactNode, useState, useEffect } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { onboardingApi } from '../../features/onboarding/onboardingApi'
import DiagnosisOnboardingModal from '../../features/onboarding/DiagnosisOnboardingModal'
import OnboardingFollowupGate from '../../features/onboarding/OnboardingFollowupGate'
import LegacyPlanNotice from '../../features/billing/LegacyPlanNotice'
import Spinner from '../ui/Spinner'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FiBell, FiUser, FiLogOut, FiChevronDown, FiAlertTriangle,
  FiMenu, FiSettings, FiChevronRight, FiSun, FiMoon, FiGlobe,
  FiBriefcase, FiClipboard,
} from 'react-icons/fi'
import { complianceApi, sitesApi } from '../../lib/sitesApi'
import Sidebar from './Sidebar'
import DashboardFooter from './DashboardFooter'
import i18n from '../../features/i18n'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import UndeliverableEmailModal from '../common/UndeliverableEmailModal'
import { useAuth } from '../../hooks/useAuth'
import { useThemeStore } from '../../store/themeStore'
import { cn } from '../../lib/utils'
// import OnboardingTour from '../../features/onboarding/OnboardingTour'  // 비활성화 — 사업장 관리 기반으로 재설계 예정
import UpgradeModal from '../common/UpgradeModal'
import SiteRegistrationGate from '../common/SiteRegistrationGate'
import PaidFeatureModal from '../common/PaidFeatureModal'
import FloatingChatWidget from '../common/FloatingChatWidget'
import { useQuotaModalStore } from '../../store/quotaModalStore'

// ──────────────────────────────────────────────
// 브레드크럼 경로 매핑 — i18n 키로 관리
// ──────────────────────────────────────────────
const pathI18nKeys: Record<string, string> = {
  dashboard: 'nav.dashboard',
  chatbot: 'nav.chatbot',
  jsa: 'nav.jsa',
  tbm: 'nav.tbm',
  'image-analysis': 'nav.imageAnalysis',
  corrective: 'nav.corrective',
  'risk-assessment': 'nav.riskAssessment',
  templates: 'nav.templates',
  billing: 'nav.billing',
  invoices: 'appShell.breadcrumb.invoices',
  settings: 'nav.settings',
  usage: 'appShell.breadcrumb.usage',
  admin: 'appShell.breadcrumb.admin',
}

function useBreadcrumbs() {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const segments = pathname.split('/').filter(Boolean)
  return segments.map((seg, idx) => {
    const key = pathI18nKeys[seg]
    return {
      label: key ? t(key, { defaultValue: seg }) : seg,
      to: '/' + segments.slice(0, idx + 1).join('/'),
      isLast: idx === segments.length - 1,
    }
  })
}

// ──────────────────────────────────────────────
// AppShell — 로그인 후 공통 레이아웃
// AppShell에 children prop이 오는 경우와 <Outlet/> 둘 다 지원
// ──────────────────────────────────────────────
interface AppShellProps {
  children?: ReactNode
}

export default function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { pathname: currentPath } = useLocation()
  const navigate = useNavigate()

  // 로그인 직후 자동 온보딩 — 서버 플래그(onboarding_completed_at)가 authoritative.
  // 게이트 방식: 상태 확인 전엔 페이지 콘텐츠 렌더를 막아(스피너) SiteEntry 등의 리다이렉트와
  // 경쟁하지 않게 한다. 미완료(NULL)면 무조건 /dashboard/onboarding 로. (localStorage 의존 X)
  const onOnboardingPage = currentPath.startsWith('/dashboard/onboarding')
  // ★ 발송불가(바운스) 팝업이 우선. 두 모달이 동시에 뜨면 진단 모달(HeadlessUI Dialog)의
  //   focus trap 이 자기 DOM 바깥을 inert 로 만들어, 위에 그려진 반송 팝업(z-[300] 일반 div)이
  //   보이기만 하고 입력·클릭이 전부 막힌다. 순서상으로도 메일이 닿아야(인증·법정 알림)
  //   온보딩이 의미가 있으므로 이메일부터 고치게 한다.
  const emailBlocked = !!user?.email_undeliverable
  const onbGateEnabled = !!user && !onOnboardingPage && !emailBlocked
  const { data: onbStatus, isLoading: onbLoading } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: () => onboardingApi.status(),
    enabled: onbGateEnabled,
    staleTime: Infinity,
    retry: false,
  })
  const needsOnboarding = onbGateEnabled && onbStatus != null && !onbStatus.completed
  // 로딩 중에만 콘텐츠를 막는다(에러 시엔 fail-open: 무한 스피너 방지).
  const onbWaiting = onbGateEnabled && onbLoading

  // 온보딩(진단 모달)은 **첫 사업장에만**(2026-08-19 지시). 이미 사업장을 2개 이상
  // 가진 계정(대행 세팅·레거시)은 조용히 완료 처리하고 모달을 띄우지 않는다 —
  // 다른 사업장 추가는 SiteWizard 의 몫이지 진단이 다시 낄 자리가 아니다.
  const qc = useQueryClient()
  const { data: onbSites } = useQuery({
    queryKey: ['sites', 'onb-count'],
    queryFn: () => sitesApi.list(),
    enabled: needsOnboarding,
    staleTime: Infinity,
    retry: false,
  })
  const skipOnbMultiSite = needsOnboarding && (onbSites?.length ?? 0) >= 2
  useEffect(() => {
    if (!skipOnbMultiSite) return
    onboardingApi.complete({ skipped: true })
      .then(() => qc.invalidateQueries({ queryKey: ['onboarding-status'] }))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipOnbMultiSite])

  // P5: 컴플라이언스 임박/경과 의무 알림 (전 사업장)
  const { data: notif } = useQuery({
    queryKey: ['compliance', 'upcoming-user'],
    queryFn: () => complianceApi.upcomingForUser(30),
    enabled: !!user,
    staleTime: 60_000,
  })
  const notifItems = notif?.items ?? []
  const notifCount = notif?.total ?? 0
  const { logout } = useAuth()
  const breadcrumbs = useBreadcrumbs()
  const { mode: themeMode, toggle: toggleTheme } = useThemeStore()

  // 대시보드 진입 시 DOM 테마를 스토어와 동기화(스탠다드=라이트).
  // 공개 페이지(ForceDarkTheme)에서 들어와도 다크가 새어들지 않게 보장.
  useEffect(() => {
    const root = document.documentElement
    if (themeMode === 'light') root.classList.add('light')
    else root.classList.remove('light')
  }, [themeMode])

  // 전역 quota 초과 모달 — 어느 컴포넌트에서나 useQuotaModalStore.open() 으로 트리거.
  const quotaModal = useQuotaModalStore()

  // 데스크톱 사이드바 접힘 상태 — 항상 '접힘'으로 강제(사용자 요청 2026-07-18).
  // 세션 내에서 토글로 잠깐 펼칠 수는 있으나 저장하지 않아, 새로고침/재진입 시 다시 접힌다.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(true)
  // 모바일 드로어 상태
  const [mobileOpen, setMobileOpen] = useState(false)
  // 알림 드롭다운
  const [notifOpen, setNotifOpen] = useState(false)
  // 유저 메뉴
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  // 1024px 이하로 줄면 사이드바 자동 접기(넓어져도 강제로 펼치지는 않음 — 저장된 선택/기본 접힘 유지)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)')
    const handle = (e: MediaQueryListEvent) => {
      if (e.matches) setSidebarCollapsed(true)
    }
    if (mq.matches) setSidebarCollapsed(true)
    mq.addEventListener('change', handle)
    return () => mq.removeEventListener('change', handle)
  }, [])

  return (
    <div className="flex h-screen bg-surface-deep overflow-hidden">
      {/* 발송 불가(바운스) 이메일 계정 — 새 주소 입력 팝업(블로킹) */}
      <UndeliverableEmailModal />
      {/* 사이드바 */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* 메인 영역 */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* ─── 상단 헤더 ─── */}
        <header className="h-14 shrink-0 bg-surface border-b border-surface-elevated/40 flex items-center justify-between px-4 gap-3">
          {/* 좌측: 모바일 햄버거 + 브레드크럼 */}
          <div className="flex items-center gap-2 min-w-0">
            {/* 모바일 햄버거 */}
            <button
              className="md:hidden p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-card transition-colors"
              onClick={() => setMobileOpen(true)}
              aria-label="메뉴 열기"
            >
              <FiMenu size={20} />
            </button>

            {/* 브레드크럼 */}
            <nav aria-label="breadcrumb" className="hidden sm:flex items-center gap-1 text-sm min-w-0">
              {breadcrumbs.map((crumb) =>
                crumb.isLast ? (
                  <span key={crumb.to} className="font-semibold text-ink truncate">
                    {crumb.label}
                  </span>
                ) : (
                  <span key={crumb.to} className="flex items-center gap-1 text-ink-faint">
                    <Link to={crumb.to} className="hover:text-ink-muted transition-colors">
                      {crumb.label}
                    </Link>
                    <FiChevronRight size={12} />
                  </span>
                ),
              )}
            </nav>
          </div>

          {/* 우측: 자체감사 토글 + 우리 회사 양식 + 언어 토글 + 테마 토글 + 알림 + 유저 메뉴 */}
          <div className="flex items-center gap-2">
            {/* PSM 자체감사 모드 토글 — 지정 감사자(psm_access)에게만.
                기존엔 사업장 홈 안쪽 배너뿐이라 컨설팅 계정이 입구를 못 찾았다(2026-08-09).
                일반 안전관리 ↔ 자체감사 작업공간을 상단에서 한 번에 오간다. */}
            {user?.psm_access && (
              <div className="inline-flex items-center rounded-lg border border-brand-secondary/40 bg-surface-card p-0.5 text-xs font-semibold whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className={`px-2.5 py-1 rounded-md transition-colors ${
                    currentPath.startsWith('/dashboard/psm-audit')
                      ? 'text-ink-muted hover:text-ink'
                      : 'bg-brand-primary/15 text-brand-primary'
                  }`}
                >
                  안전관리
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/psm-audit')}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors ${
                    currentPath.startsWith('/dashboard/psm-audit')
                      ? 'bg-brand-secondary/20 text-brand-primary dark:text-brand-secondary-light'
                      : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  <FiClipboard size={12} aria-hidden="true" /> PSM 자체감사
                </button>
              </div>
            )}

            {/* 우리 회사 양식 사용하기 — B2B 엔터프라이즈 맞춤 양식 안내 페이지 */}
            <Link
              to="/dashboard/enterprise/custom-format"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                         text-amber-700 dark:text-amber-300
                         bg-amber-500/10 hover:bg-amber-500/20
                         border border-amber-500/30 rounded-lg transition-colors
                         whitespace-nowrap"
              title="우리 회사 양식 그대로 — Enterprise 맞춤 자동 생성 안내"
            >
              <FiBriefcase size={13} aria-hidden="true" />
              <span>우리 회사 양식 사용하기</span>
            </Link>

            {/* 언어 토글 */}
            <button
              type="button"
              onClick={async () => {
                const next = i18n.language === 'ko' ? 'en' : 'ko'
                await i18n.changeLanguage(next)
                try { await api.patch('/auth/me', { locale: next }) } catch { /* 실패해도 UI 는 전환 유지 */ }
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-ink-muted hover:text-ink rounded-lg hover:bg-surface-card transition-colors text-xs font-medium"
              aria-label={t('appShell.toggleLanguage', { defaultValue: '언어 전환' })}
              title={t('appShell.toggleLanguage', { defaultValue: '언어 전환' })}
            >
              <FiGlobe size={14} aria-hidden="true" />
              <span className="uppercase">{i18n.language === 'ko' ? 'EN' : 'KO'}</span>
            </button>

            {/* 다크/라이트 테마 토글 */}
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 text-ink-muted hover:text-ink rounded-lg hover:bg-surface-card transition-colors"
              aria-label={themeMode === 'dark'
                ? t('appShell.theme.toLight', { defaultValue: '라이트 모드로 전환' })
                : t('appShell.theme.toDark', { defaultValue: '다크 모드로 전환' })}
              title={themeMode === 'dark'
                ? t('appShell.theme.light', { defaultValue: '라이트 모드' })
                : t('appShell.theme.dark', { defaultValue: '다크 모드' })}
            >
              {themeMode === 'dark' ? <FiSun size={18} /> : <FiMoon size={18} />}
            </button>

            {/* 알림 벨 */}
            <div className="relative">
              <button
                className="relative p-2 text-ink-muted hover:text-ink rounded-lg hover:bg-surface-card transition-colors"
                aria-label="알림"
                onClick={() => { setNotifOpen((o) => !o); setUserMenuOpen(false) }}
              >
                <FiBell size={18} />
                {/* 미읽은 뱃지 — 임박/경과 의무 수 */}
                {notifCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {notifCount > 9 ? '9+' : notifCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {notifOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setNotifOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 z-20 w-80 bg-surface-card border border-surface-elevated/50 rounded-md shadow-card-dark overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-surface-elevated/40 flex items-center justify-between">
                        <p className="text-sm font-semibold text-ink">법정 의무 알림</p>
                        {notifCount > 0 && (
                          <span className="text-xs text-red-500 font-medium">{notif?.overdue ?? 0} 경과 · {notif?.due_soon ?? 0} 임박</span>
                        )}
                      </div>
                      {notifItems.length === 0 ? (
                        <div className="py-8 text-center"><p className="text-xs text-ink-faint">처리할 임박 의무가 없습니다 ✓</p></div>
                      ) : (
                        <div className="max-h-80 overflow-y-auto divide-y divide-surface-elevated/30">
                          {notifItems.slice(0, 8).map((it) => (
                            <Link key={it.obligation_id} to={`/dashboard/site/${it.site_id}`}
                              onClick={() => setNotifOpen(false)}
                              className="flex items-start gap-2.5 px-4 py-2.5 hover:bg-surface-elevated/20 transition-colors">
                              <FiAlertTriangle size={14}
                                className={`mt-0.5 shrink-0 ${it.status === 'overdue' ? 'text-red-500' : 'text-amber-500'}`} />
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium text-ink truncate">{it.title}</div>
                                <div className="text-[11px] text-ink-muted truncate">{it.site_name}</div>
                                <div className={`text-[11px] mt-0.5 ${it.days_remaining < 0 ? 'text-red-500' : 'text-amber-500'}`}>
                                  {it.days_remaining < 0 ? `${-it.days_remaining}일 경과` : it.days_remaining === 0 ? '오늘 마감' : `D-${it.days_remaining}`}
                                  {it.next_due_at && ` · ${it.next_due_at}`}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* 사용자 드롭다운 */}
            <div className="relative">
              <button
                onClick={() => { setUserMenuOpen((o) => !o); setNotifOpen(false) }}
                className={cn(
                  'flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors',
                  'text-sm font-medium text-ink-muted hover:text-ink hover:bg-surface-card',
                )}
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.display_name ?? 'user'}
                    className="h-7 w-7 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-brand-primary/20 flex items-center justify-center">
                    <FiUser size={13} className="text-brand-primary-light" />
                  </div>
                )}
                <span className="hidden md:block max-w-[110px] truncate text-xs">
                  {user?.display_name ?? t('appShell.anonymousUser', { defaultValue: '사용자' })}
                </span>
                <FiChevronDown size={12} />
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 z-20 w-52 bg-surface-card border border-surface-elevated/50 rounded-md shadow-card-dark overflow-hidden"
                    >
                      {/* 사용자 정보 헤더 */}
                      <div className="px-4 py-3 border-b border-surface-elevated/40">
                        <div className="flex items-center gap-2.5">
                          {user?.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="h-8 w-8 rounded-full" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-brand-primary/20 flex items-center justify-center">
                              <FiUser size={14} className="text-brand-primary-light" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink truncate">{user?.display_name}</p>
                            <p className="text-xs text-ink-faint truncate">{user?.email}</p>
                          </div>
                        </div>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/dashboard/settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-muted hover:text-ink hover:bg-surface-elevated/40 transition-colors"
                        >
                          <FiSettings size={14} />
                          {t('nav.settings')}
                        </Link>
                        <Link
                          to="/dashboard/billing"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-muted hover:text-ink hover:bg-surface-elevated/40 transition-colors"
                        >
                          <FiBell size={14} />
                          {t('nav.billing')}
                        </Link>
                        <div className="mx-3 my-1 h-px bg-surface-elevated/40" />
                        <button
                          onClick={() => { setUserMenuOpen(false); logout() }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-zinc-900 dark:text-white hover:bg-red-500/10 transition-colors"
                        >
                          <FiLogOut size={14} />
                          {t('auth.logout')}
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* ─── 온보딩 투어 — 비활성화(사업장 관리 기반으로 재설계 예정) ─── */}
        {/* <OnboardingTour /> */}

        {/* ─── 페이지 콘텐츠 ─── */}
        <main className="flex-1 overflow-y-auto flex flex-col" id="main-content">
          <div className="flex-1 p-4 md:p-6">
            {/* 온보딩 중엔 사업장 등록 게이트 억제(진단 모달이 사업장 생성을 담당) */}
            {!onOnboardingPage && !needsOnboarding && !emailBlocked && <SiteRegistrationGate />}
            {/* 온보딩 후속 1회 흐름(RA 항목 검토 재노출 → 첫 이관 팝업) — 어느 페이지로
                돌아와도 만나도록 전역 배치(이탈자 랜딩은 /dashboard 홈이었다, 실측). */}
            {!onOnboardingPage && !needsOnboarding && !emailBlocked && !!user && <OnboardingFollowupGate />}
            {/* children prop (legacy) 또는 Outlet */}
            {onbWaiting
              ? <div className="flex justify-center py-24"><Spinner size="lg" /></div>
              : (children ?? <Outlet />)}
          </div>
          <DashboardFooter />
        </main>
      </div>

      {/* 2분 진단 온보딩 — 가짜 데모 투어 대체. 건너뛰기 불가 팝업. 완료 시 사업장 홈으로. */}
      {needsOnboarding && onbSites != null && !skipOnbMultiSite && (
        <DiagnosisOnboardingModal open onComplete={() => navigate('/dashboard/onboarding')} />
      )}

      {/* 요금개편 그랜드파더링 안내(086) — 기존 유료 구독자(*_legacy) 1회 팝업 */}
      {!needsOnboarding && <LegacyPlanNotice />}

      {/* 전역 quota 초과 모달 — 사이드바·대시보드 카드 등에서 navigate 차단 후 노출 */}
      <UpgradeModal
        isOpen={quotaModal.isOpen}
        onClose={quotaModal.close}
        feature={quotaModal.feature}
        quotaInfo={quotaModal.info}
      />

      {/* 우측 하단 AI Chat 위젯 — 챗봇/Chat Studio 페이지 제외 모든 페이지 (dev) */}
      <FloatingChatWidget />
      <PaidFeatureModal />
    </div>
  )
}
